import { useAuth0 } from '@auth0/auth0-react';
import * as Sentry from '@sentry/react';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { userHelpers } from 'src/utils/userHelpers';

import { authHelpers } from 'src/utils/authHelpers';
import { enterpriseHelpers } from 'src/utils/enterpriseHelpers';
import { useFeatureFlags } from 'src/utils/useFeatureFlags';
import type { cloudEnterpriseApi } from '../endpoints';
import { cloudEnterpriseAdminApi } from '../endpoints';
import { queryFleetMembers } from './fleet';
import { queryKeys } from './queryKeys';
import { useAccessTokenClaims } from './user';

export const queryEnterpriseRoles = () =>
  queryOptions({
    queryKey: queryKeys.enterpriseAdmin.roles,
    queryFn: () => cloudEnterpriseAdminApi.getRoles(),
    staleTime: 10_000 * 60, // 10 minutes
  });

export const useAddRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { memberId: string; roleId: string }) =>
      toast.promise(cloudEnterpriseAdminApi.addRoleOnUser(vars), {
        loading: 'Adding role...',
        error: 'Unable to add role',
        success: 'Role added',
      }),
    onSettled() {
      return queryClient.invalidateQueries(queryFleetMembers());
    },
  });
};

export const useDeleteRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { memberId: string; roleId: string }) =>
      toast.promise(cloudEnterpriseAdminApi.deleteRoleOnUser(vars), {
        loading: 'Removing role...',
        error: 'Unable to remove role',
        success: 'Role removed',
      }),
    onSettled() {
      return queryClient.invalidateQueries(queryFleetMembers());
    },
  });
};

export const useTransferOwnership = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { memberId: string }) =>
      cloudEnterpriseAdminApi.transferOwnership(vars),
    onSettled() {
      return queryClient.invalidateQueries(queryFleetMembers());
    },
  });
};

export const queryEntepriseOrgSettings = () =>
  queryOptions({
    queryKey: queryKeys.enterpriseAdmin.orgSettings,
    queryFn: () => cloudEnterpriseAdminApi.getOrgSettings(),
    staleTime: 10_000,
  });

export const useEnterpriseOrgSettings = () => {
  const auth = useAuth0();
  const accessToken = useAccessTokenClaims();
  return useQuery({
    ...queryEntepriseOrgSettings(),
    refetchInterval({ state }) {
      if (
        state.data?.offboardingStatus === 'DEACTIVING_USERS' ||
        state.data?.offboardingStatus === 'EXPORTING_DATA' ||
        state.data?.offboardingStatus === 'FINALIZING'
      ) {
        return 10_000; // Refetch every 10 seconds during offboarding process
      }
      return false;
    },
    enabled:
      auth.user && userHelpers.hasPermissionUpdateOrgSettings(accessToken.data),
  });
};

export const useUpdateOrgSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: { analyticsOptout: boolean }) =>
      toast.promise(cloudEnterpriseAdminApi.updateOrgSettings(vars), {
        loading: 'Updating organization settings...',
        error: 'Unable to update organization settings',
        success: 'Organization settings updated',
      }),
    onMutate(vars) {
      const previousSettings = queryClient.getQueryData(
        queryKeys.enterpriseAdmin.orgSettings
      );
      // Optimistically update to the new value
      if (previousSettings) {
        queryClient.setQueryData(queryKeys.enterpriseAdmin.orgSettings, {
          ...previousSettings,
          ...vars,
        });
      }

      return { previousSettings };
    },
    onError(_error, _vars, context) {
      // Rollback to the previous value in case of error
      if (context?.previousSettings) {
        queryClient.setQueryData(
          queryKeys.enterpriseAdmin.orgSettings,
          context.previousSettings
        );
      }
    },
  });
};

export const useUpdateOrgDisplayName = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: { displayName: string }) => {
      await toast.promise(cloudEnterpriseAdminApi.updateOrgDisplayName(vars), {
        loading: 'Updating organization name...',
        error: 'Unable to update organization name',
        success: 'Organization name updated',
      });

      // The organization name in the header is read from the token claims, so
      // a fresh token is needed for the new name to show up. The rename itself
      // has already succeeded, so a failed refresh should not fail the mutation.
      try {
        await authHelpers.getAccessTokenSilently(auth0, { cacheMode: 'off' });
      } catch (error) {
        Sentry.captureException(error);
      }
    },
    onSuccess() {
      return queryClient.invalidateQueries({
        queryKey: queryKeys.enterpriseAdmin.orgSettings,
      });
    },
  });
};

export const useEnableEnterpriseMigration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: { domain?: string }) => {
      if (!vars.domain) {
        throw new Error('No domain provided.');
      }

      return cloudEnterpriseAdminApi.enableEnterpriseMigration({
        domain: vars.domain,
      });
    },
    onSettled() {
      return queryClient.invalidateQueries({
        queryKey: queryKeys.enterpriseAdmin.orgSettings,
      });
    },
  });
};

export const useEnableEnterpriseDomainForwarding = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: { domain?: string }) => {
      if (!vars.domain) {
        throw new Error('No domain provided.');
      }
      return cloudEnterpriseAdminApi.enableEnterpriseDomainForwarding({
        domain: vars.domain,
      });
    },
    onSettled() {
      return queryClient.invalidateQueries({
        queryKey: queryKeys.enterpriseAdmin.orgSettings,
      });
    },
  });
};

export const useDeleteDomain = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { domainId: string }) =>
      toast.promise(cloudEnterpriseAdminApi.deleteDomain(vars), {
        loading: 'Deleting domain...',
        error: 'Unable to delete domain',
        success: 'Domain deleted',
      }),
    onSettled() {
      return queryClient.invalidateQueries({
        queryKey: queryKeys.enterpriseAdmin.orgSettings,
      });
    },
  });
};

export const useMakePrimaryDomain = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: { domainId: string }) =>
      toast.promise(cloudEnterpriseAdminApi.makePrimaryDomain(vars), {
        loading: 'Making primary domain...',
        error: 'Unable to make primary domain',
        success: 'Primary domain updated',
      }),
    onSettled() {
      return queryClient.invalidateQueries({
        queryKey: queryKeys.enterpriseAdmin.orgSettings,
      });
    },
  });
};

export const useDeactivateUser = () => {
  return useMutation({
    mutationFn: (vars: { memberId: string }) =>
      toast.promise(cloudEnterpriseAdminApi.deactivateUser(vars), {
        loading: 'Deactivating user...',
        error: 'Unable to deactivate user',
      }),
  });
};

export const useDeleteUser = () => {
  const featureFlags = useFeatureFlags();
  return useMutation({
    mutationFn: (vars: { memberId: string }) =>
      toast.promise(
        cloudEnterpriseAdminApi.deleteUser({
          ...vars,
          directDeleteUser: featureFlags.EnableEnterpriseDirectDeleteUser,
        }),
        {
          loading: 'Deleting user...',
          error: 'Unable to delete user',
        }
      ),
  });
};

export const useActivateUser = () => {
  return useMutation({
    mutationFn: async (vars: { memberId: string }) =>
      toast.promise(cloudEnterpriseAdminApi.activateUser(vars), {
        loading: 'Activating user...',
        error: 'Unable to Activate user',
      }),
  });
};

export const useEnableMemberMdm = () => {
  return useMutation({
    mutationFn: (vars: { memberId: string }) =>
      toast.promise(cloudEnterpriseAdminApi.enableMemberMdm(vars), {
        loading: 'Activating MDM...',
        error: 'Unable to activate MDM',
      }),
  });
};

export const useDisableMemberMdm = () => {
  return useMutation({
    mutationFn: (vars: { memberId: string }) =>
      toast.promise(cloudEnterpriseAdminApi.disableMemberMdm(vars), {
        loading: 'Deactivating MDM...',
        error: 'Unable to deactivate MDM',
      }),
  });
};

export const useStartOffboarding = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: cloudEnterpriseAdminApi.startOffboarding,
    onSettled: () => {
      return queryClient.invalidateQueries({
        queryKey: queryKeys.enterpriseAdmin.orgSettings,
      });
    },
  });
};

export const useCompleteOffboarding = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      vars: Parameters<typeof cloudEnterpriseAdminApi.completeOffboarding>[0]
    ) =>
      toast.promise(cloudEnterpriseAdminApi.completeOffboarding(vars), {
        loading: 'Completing offboarding...',
        error: 'Unable to complete offboarding',
        success: 'Offboarding completed',
      }),
    onSettled: () => {
      return queryClient.invalidateQueries({
        queryKey: queryKeys.enterpriseAdmin.orgSettings,
      });
    },
  });
};

export type MembersQueryData = Awaited<
  ReturnType<typeof cloudEnterpriseApi.getMembers>
>;

export const useDeleteDevice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      memberId: string;
      deviceId: string | undefined;
    }) => {
      if (!vars.deviceId) {
        throw new Error('No device id provided.');
      }

      return await toast.promise(
        cloudEnterpriseAdminApi.deleteDevice({
          deviceId: vars.deviceId,
          memberId: vars.memberId,
        }),
        {
          loading: 'Unpairing device...',
          error: 'Unable to unpair device',
          success: 'Device unpaired',
        }
      );
    },
    onSuccess(_, variables) {
      // Because there is an inconsistent delay between this mutation succeeding
      // and the fleet members query reflecting the change, we optimistically update
      // the fleet members query to remove the unpaired device immediately.
      queryClient.setQueryData(
        queryKeys.fleet.members,
        (oldData: MembersQueryData) =>
          enterpriseHelpers.dataHelpers.removeDeviceFromMemberData(
            oldData,
            variables.memberId,
            variables.deviceId || ''
          )
      );
    },
  });
};
